import { NotificationTypes, showNotification } from '../components/Notifications';
import { fetchJobStatus } from './fileUploadService';
import { FileUploadStatus } from './types';
import { MAX_POLL_COUNT, POLL_INTERVAL } from './constants';

/**
 * Polls the server for file processing status with timeout and retry logic
 */
export const createStatusPoller = (
  fileJobId: string,
  onStatusUpdate: (status: Partial<FileUploadStatus>) => void,
  onComplete: (presignedUrl: string) => void,
) => {
  let pollCount = 0;

  const pollStatus = async (): Promise<void> => {
    if (!fileJobId) {
      console.error('File job ID is not set. Cannot poll status.');
      showNotification(NotificationTypes.error, 'File Job ID Missing', 'File job ID is not set. Cannot poll status.');
      onStatusUpdate({
        isUploading: false,
        uploadMessage: 'File job ID missing',
      });
      return;
    }

    try {
      const statusResponse = await fetchJobStatus(fileJobId);
      console.log(`Polling status response ${pollCount}:`, statusResponse);

      pollCount++;

      if (statusResponse?.status === 'COMPLETED') {
        showNotification(
          NotificationTypes.success,
          'File Imported Successfully',
          'The file has been imported successfully.',
        );

        onStatusUpdate({
          isUploading: false,
          fileName: '',
          uploadMessage: 'File processed successfully',
          lastUpload: new Date(),
        });

        if (statusResponse.presignedUrl) {
          onComplete(statusResponse.presignedUrl);
        }
        return;
      }

      if (statusResponse?.status === 'FAILED') {
        showNotification(
          NotificationTypes.error,
          'File Processing Failed',
          'The file processing failed. Please check the file and try again.',
        );
        onStatusUpdate({
          isUploading: false,
          uploadMessage: 'File processing failed',
        });
        return;
      }

      if (pollCount >= MAX_POLL_COUNT) {
        showNotification(
          NotificationTypes.warning,
          'Processing Timeout',
          'File processing is taking longer than expected. Please check back later.',
        );
        onStatusUpdate({
          isUploading: false,
          uploadMessage: 'Processing timeout - check back later',
        });
        return;
      }

      // Continue polling
      setTimeout(pollStatus, POLL_INTERVAL);
    } catch (apiError) {
      console.error('Error fetching job status:', apiError);
      showNotification(
        NotificationTypes.error,
        'Error Checking Job Status',
        'Failed to get the job status. Please try again later.',
      );
      onStatusUpdate({
        isUploading: false,
        uploadMessage: 'Error processing file',
      });
    }
  };

  return pollStatus;
};

/**
 * Checks and updates the connection status based on file upload status
 */
export const checkConnectionStatus = (fileUploadStatus: FileUploadStatus): boolean => {
  return !!fileUploadStatus.lastUpload || fileUploadStatus.uploadMessage === 'File processed successfully';
};

